import { Table, Button, Tag, Space, Popconfirm, message, Select, notification } from "antd";
import DatePicker from "../../../../../components/inputs/MomentDatePicker";
import moment from "moment";
import React, { useState, useMemo, useCallback } from "react";
import { useQueryClient } from "react-query";
import {
  useGetAllPermissions,
  useUpdatePermissionStatus,
} from "../../../../../services/permissions.service";
import { useStaffGroup } from "./StaffGroupContext";

const { RangePicker } = DatePicker;
const { Option } = Select;

const AdminLeaves = () => {
  const group = useStaffGroup();
  const queryClient = useQueryClient();
  const [dateRange, setDateRange] = useState([
    moment().subtract(7, "days"),
    moment(),
  ]);
  const [typeFilter, setTypeFilter] = useState("all");
  const [api, contextHolder] = notification.useNotification();

  const leaveParams = useMemo(() => {
    const p = {
      start_date: dateRange[0]?.format("YYYY-MM-DD"),
      end_date: dateRange[1]?.format("YYYY-MM-DD"),
      type: "leave",
      group,
    };
    console.log("Leaves.js - leaveParams:", p);
    return p;
  }, [dateRange, group]);

  const sickParams = useMemo(() => {
    const p = {
      start_date: dateRange[0]?.format("YYYY-MM-DD"),
      end_date: dateRange[1]?.format("YYYY-MM-DD"),
      type: "sick",
      group,
    };
    console.log("Leaves.js - sickParams:", p);
    return p;
  }, [dateRange, group]);

  const {
    data: leaveData,
    isLoading: leaveLoading,
    refetch: refetchLeaves,
  } = useGetAllPermissions({}, leaveParams);

  const {
    data: sickData,
    isLoading: sickLoading,
    refetch: refetchSick,
  } = useGetAllPermissions({}, sickParams);

  const data = useMemo(() => {
    let allData = [...(leaveData || []), ...(sickData || [])];

    allData.sort((a, b) => {
      const dateA = moment(a.from_date || a.from_time || a.created_at);
      const dateB = moment(b.from_date || b.from_time || b.created_at);
      return dateB.valueOf() - dateA.valueOf();
    });

    if (typeFilter && typeFilter !== "all") {
      return allData.filter((item) => item.type === typeFilter);
    }

    console.log("Leaves.js - Combined data:", allData.length, "items");
    return allData;
  }, [leaveData, sickData, typeFilter]);

  const isLoading = leaveLoading || sickLoading;

  const updatePermission = useUpdatePermissionStatus({
    onSuccess: (response, variables) => {
      const { requestId, data: updateData } = variables;
      const leave = data.find((item) => item._id === requestId);

      if (leave) {
        const statusText =
          updateData.status === "approved" ? "Approved" : "Rejected";

        message.success(`Leave ${statusText.toLowerCase()} successfully`);
      }

      queryClient.invalidateQueries("allPermissions");
      refetchLeaves();
      refetchSick();
    },
    onError: (error) => {
      message.error(error?.response?.data?.detail || "Failed to update leave");
    },
  });

  const handleApprove = useCallback(
    (id, record) => {
      updatePermission.mutate({
        requestId: id,
        data: { status: "approved" },
      });
    },
    [updatePermission]
  );

  const handleDecline = useCallback(
    (id, record) => {
      updatePermission.mutate({
        requestId: id,
        data: { status: "rejected" },
      });
    },
    [updatePermission]
  );

  const getTypeColor = (type) => {
    switch (type) {
      case "sick":
        return "red";
      case "leave":
        return "orange";
      default:
        return "default";
    }
  };

  const getTypeDisplayName = (type) => {
    switch (type) {
      case "sick":
        return "Sick Leave";
      case "leave":
        return "Casual Leave";
      default:
        return type;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "green";
      case "pending":
        return "orange";
      case "rejected":
        return "red";
      default:
        return "default";
    }
  };

  const [dates, setDates] = useState(null);

  const disabledDate = (current) => {
    if (!dates || dates.length === 0) {
      return false;
    }
    const tooLate = dates[0] && current.diff(dates[0], "days") > 13;
    const tooEarly = dates[0] && dates[0].diff(current, "days") > 13;
    return !!tooEarly || !!tooLate;
  };

  const onOpenChange = (open) => {
    if (open) {
      setDates([]);
    }
  };

  const handleDateChange = (val) => {
    console.log("Date range changed to:", val);
    setDateRange(val);
  };

  const handleTypeFilterChange = (value) => {
    console.log("Type filter changed to:", value);
    setTypeFilter(value);
  };

  const columns = useMemo(
    () => [
      {
        title: "Date Range",
        key: "date_range",
        align: "center",
        render: (_, record) => {
          if (record.from_date && record.to_date) {
            const startDate = moment(record.from_date).format("DD/MM/YYYY");
            const endDate = moment(record.to_date).format("DD/MM/YYYY");

            if (startDate === endDate) {
              return (
                <div className="font-semibold">
                  <div>{startDate}</div>
                </div>
              );
            }

            return (
              <div className="font-semibold">
                <div>
                  {startDate} to {endDate}
                </div>
              </div>
            );
          } else {
            return (
              <div className="font-semibold">
                {record.from_time
                  ? moment(record.from_time).format("DD/MM/YYYY")
                  : moment(record.created_at).format("DD/MM/YYYY")}
              </div>
            );
          }
        },
      },
      {
        title: "Instructor",
        dataIndex: "instructor_name",
        key: "name",
        align: "center",
        render: (text) => <div className="capitalize">{text || "Unknown"}</div>,
      },
      {
        title: "Type",
        dataIndex: "type",
        key: "type",
        align: "center",
        render: (text) => (
          <Tag color={getTypeColor(text)} className="capitalize">
            {getTypeDisplayName(text)}
          </Tag>
        ),
      },
      {
        title: "Duration",
        dataIndex: "num_days",
        key: "leave_duration",
        align: "center",
        render: (num_days, record) => {
          if (record.leave_duration === "half_day_morning") {
            return (
              <span className="text-gray-700 font-semibold">
                Half Day (Morning)
              </span>
            );
          }
          if (record.leave_duration === "half_day_afternoon") {
            return (
              <span className="text-gray-700 font-semibold">
                Half Day (Evening)
              </span>
            );
          }
          // Fallback: compute from dates if num_days is missing (old records / mobile)
          const resolvedDays =
            num_days != null
              ? num_days
              : record.from_date && record.to_date
              ? moment(record.to_date).diff(moment(record.from_date), "days") + 1
              : null;
          return (
            <span className="text-gray-700 font-semibold">
              {record.leave_duration === "half_day" || resolvedDays === 0.5
                ? record.hours || "Half Day"
                : resolvedDays != null
                ? `${resolvedDays} day${resolvedDays !== 1 ? "s" : ""}`
                : "—"}
            </span>
          );
        },
      },
      {
        title: "Reason",
        dataIndex: "reason",
        key: "reason",
        align: "center",
        render: (text) => <span className="text-gray-600">{text || "-"}</span>,
      },
      {
        title: "Status",
        key: "status",
        align: "center",
        render: (_, record) => (
          <Tag
            color={getStatusColor(record.status)}
            className="capitalize px-3 py-1"
          >
            {record.status === "approved"
              ? "Approved"
              : record.status === "rejected"
              ? "Rejected"
              : "Pending"}
          </Tag>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        align: "center",
        render: (_, record) => {
          if (record.status === "approved" || record.status === "rejected") {
            return (
              <Tag
                color={getStatusColor(record.status)}
                className="capitalize px-3 py-1"
              >
                {record.status === "approved" ? "Approved" : "Rejected"}
              </Tag>
            );
          }

          return (
            <Space size="middle">
              <Popconfirm
                title="Approve Leave Request"
                description={`Approve ${
                  record.instructor_name || "instructor"
                }'s leave request? This will send them a push notification.`}
                onConfirm={() => handleApprove(record._id, record)}
                okText="Yes, Approve"
                cancelText="Cancel"
                okButtonProps={{ type: "primary" }}
              >
                <Button type="primary" size="small">
                  Approve
                </Button>
              </Popconfirm>
              <Popconfirm
                title="Reject Leave Request"
                description={`Reject ${
                  record.instructor_name || "instructor"
                }'s leave request? This will send them a push notification.`}
                onConfirm={() => handleDecline(record._id, record)}
                okText="Yes, Reject"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
              >
                <Button type="primary" danger size="small">
                  Reject
                </Button>
              </Popconfirm>
            </Space>
          );
        },
      },
    ],
    [handleApprove, handleDecline]
  );

  return (
    <div className="mt-8">
      {contextHolder}
      <div className="bg-white drop-shadow-md p-6 rounded-lg">
        <div className="text-xl text-orange-500 font-semibold py-4">
          Admin - Leaves Management
        </div>
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-4">
            <span className="text-base font-semibold">
              Select Date Range: &nbsp;
            </span>
            <RangePicker
              format="DD/MM/YYYY"
              value={dateRange}
              dates={dates}
              onCalendarChange={(val) => setDates(val)}
              onChange={handleDateChange}
              onOpenChange={onOpenChange}
              disabledDate={disabledDate}
              allowClear={false}
              style={{ width: 300 }}
            />
            <span className="text-base font-semibold">
              Filter by Type: &nbsp;
            </span>
            <Select
              value={typeFilter}
              onChange={handleTypeFilterChange}
              style={{ width: 150 }}
              placeholder="All Types"
              allowClear
            >
              <Option value="all">All Types</Option>
              <Option value="leave">Casual Leave</Option>
              <Option value="sick">Sick Leave</Option>
            </Select>
          </div>
        </div>
        <Table
          className="rounded-2xl pt-6"
          columns={columns}
          dataSource={data?.length > 0 ? data : []}
          loading={isLoading}
          rowKey="_id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
        />
      </div>
    </div>
  );
};

export default AdminLeaves;
